/**
 * Admin: Talent Billing Roll-up
 *
 * Surfaces /api/talent-billing/admin/rollup — one row per talent with
 * AI credit balance, total charged, total credited, net balance, dues
 * owed. Sortable by any column so admins can spot under-funded or
 * past-due talent at a glance.
 */
import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, ArrowUpDown, AlertTriangle } from "lucide-react";

interface RollupRow {
  talentId: string;
  talentName: string;
  stripeCustomerId: string | null;
  aiCreditsBalanceCents: number;
  totalCharged: number;
  totalCredited: number;
  netBalance: number;
  duesOwedCents: number;
}

type SortKey = "name" | "balance" | "charged" | "credited" | "dues";

function fmtCredits(c: number | null | undefined): string {
  if (c == null) return "—";
  return c.toLocaleString("en-US") + " cr";
}

export function TalentBillingRollupCard() {
  const [sortBy, setSortBy] = useState<SortKey>("dues");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const { data: rows = [], isLoading, error } = useQuery<RollupRow[]>({
    queryKey: ["/api/talent-billing/admin/rollup"],
    queryFn: async () => {
      const r = await fetch("/api/talent-billing/admin/rollup", {
        credentials: "include",
      });
      if (!r.ok) throw new Error("Failed to load roll-up");
      return r.json();
    },
  });

  const sorted = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      switch (sortBy) {
        case "name":
          return a.talentName.localeCompare(b.talentName) * dir;
        case "balance":
          return (a.aiCreditsBalanceCents - b.aiCreditsBalanceCents) * dir;
        case "charged":
          return (a.totalCharged - b.totalCharged) * dir;
        case "credited":
          return (a.totalCredited - b.totalCredited) * dir;
        case "dues":
        default:
          return (a.duesOwedCents - b.duesOwedCents) * dir;
      }
    });
    return arr;
  }, [rows, sortBy, sortDir]);

  function toggle(col: SortKey) {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(col);
      setSortDir("desc");
    }
  }

  const totals = useMemo(
    () => ({
      ai: rows.reduce((s, r) => s + r.aiCreditsBalanceCents, 0),
      charged: rows.reduce((s, r) => s + r.totalCharged, 0),
      credited: rows.reduce((s, r) => s + r.totalCredited, 0),
      dues: rows.reduce((s, r) => s + r.duesOwedCents, 0),
    }),
    [rows],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8" data-testid="loader-rollup">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Talent Billing Roll-up</CardTitle>
        <CardDescription>
          Per-talent AI credit balance, spend, and dues owed — all values in credits.
          Click column headers to sort.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error ? (
          <p className="text-sm text-destructive" data-testid="text-rollup-error">
            Failed to load roll-up.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <Stat label="Total AI Balance" value={fmtCredits(totals.ai)} />
              <Stat label="Total Charged" value={fmtCredits(totals.charged)} />
              <Stat label="Total Credited" value={fmtCredits(totals.credited)} />
              <Stat
                label="Total Dues Owed"
                value={fmtCredits(totals.dues)}
                warn={totals.dues > 0}
              />
            </div>

            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortHead label="Talent" onClick={() => toggle("name")} active={sortBy === "name"} dir={sortDir} />
                    <SortHead label="AI Balance" onClick={() => toggle("balance")} active={sortBy === "balance"} dir={sortDir} align="right" />
                    <SortHead label="Charged" onClick={() => toggle("charged")} active={sortBy === "charged"} dir={sortDir} align="right" />
                    <SortHead label="Credited" onClick={() => toggle("credited")} active={sortBy === "credited"} dir={sortDir} align="right" />
                    <SortHead label="Dues Owed" onClick={() => toggle("dues")} active={sortBy === "dues"} dir={sortDir} align="right" />
                    <TableHead className="text-right">Stripe</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground" data-testid="text-rollup-empty">
                        No talent yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    sorted.map((r) => {
                      const lowBalance = r.aiCreditsBalanceCents < 500;
                      return (
                        <TableRow key={r.talentId} data-testid={`row-rollup-${r.talentId}`}>
                          <TableCell className="font-medium">{r.talentName}</TableCell>
                          <TableCell className={`text-right ${lowBalance ? "text-destructive" : ""}`}>
                            {fmtCredits(r.aiCreditsBalanceCents)}
                            {lowBalance && (
                              <AlertTriangle className="h-3 w-3 inline ml-1" />
                            )}
                          </TableCell>
                          <TableCell className="text-right">{fmtCredits(r.totalCharged)}</TableCell>
                          <TableCell className="text-right">{fmtCredits(r.totalCredited)}</TableCell>
                          <TableCell className={`text-right ${r.duesOwedCents > 0 ? "font-semibold text-destructive" : ""}`}>
                            {fmtCredits(r.duesOwedCents)}
                          </TableCell>
                          <TableCell className="text-right">
                            {r.stripeCustomerId ? (
                              <Badge variant="outline" className="text-xs">linked</Badge>
                            ) : (
                              <Badge variant="secondary" className="text-xs">—</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({
  label,
  value,
  warn = false,
}: {
  label: string;
  value: string;
  warn?: boolean;
}) {
  return (
    <div className="border rounded-md p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-xl font-semibold ${warn ? "text-destructive" : ""}`}>
        {value}
      </p>
    </div>
  );
}

function SortHead({
  label,
  onClick,
  active,
  dir,
  align = "left",
}: {
  label: string;
  onClick: () => void;
  active: boolean;
  dir: "asc" | "desc";
  align?: "left" | "right";
}) {
  return (
    <TableHead className={align === "right" ? "text-right" : ""}>
      <Button
        variant="ghost"
        size="sm"
        onClick={onClick}
        className={`h-6 px-1 ${active ? "font-semibold" : ""}`}
        data-testid={`sort-${label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        {label}
        <ArrowUpDown className={`h-3 w-3 ml-1 ${active ? "opacity-100" : "opacity-30"}`} />
      </Button>
    </TableHead>
  );
}
